import { ApiError } from "@repo/api-client";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  customDomainApi,
  customDomainKeys,
  type CustomDomainResponse,
  type CustomDomainStatus,
} from "./customDomainApi";

const section = css({
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  padding: "4",
  marginBottom: "4",
});
const row = css({ display: "flex", flexDirection: "column", gap: "1", marginBottom: "3" });
const actions = css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" });
const errText = css({ color: "fg.error", fontSize: "sm" });
const muted = css({ fontSize: "sm", color: "fg.muted" });
const code = css({ fontFamily: "mono", fontSize: "sm", wordBreak: "break-all" });
const dl = css({ display: "grid", gridTemplateColumns: "auto 1fr", gap: "1 3", marginTop: "2" });

/** The four non-live statuses whose panel shows the DNS setup instructions + a spinner-ish "checking". */
const PENDING_STATES: ReadonlySet<CustomDomainStatus> = new Set([
  "pending",
  "verifying",
  "securing",
]);

/**
 * The venue-settings custom-domain panel (custom-domain.md Scope, R6). Shows the current status
 * (pending/verifying/securing/live/error) with plain-language DNS guidance, a connect form, and a
 * disconnect control. Verification is ASYNC on the backend (a ShedLock job) — this panel just polls the
 * status query while pending (SSE also nudges it via the venue topic). NO certificate/file field exists
 * (R4). A 409 (hostname taken elsewhere / lost race) and a 403 (ENTITLEMENT_REQUIRED) map to inline,
 * RGAA-clean messages, never a dead end. Mounted inside the site editor page.
 */
export function CustomDomainPanel({ venueId }: { venueId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hostname, setHostname] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: customDomainKeys.domain(venueId),
    queryFn: () => customDomainApi.get(venueId),
    // Poll while a connection is mid-verification so the manager sees it go live without a manual reload.
    refetchInterval: (q) =>
      q.state.data && PENDING_STATES.has(q.state.data.status) ? 8000 : false,
  });
  const domain = query.data ?? null;

  function mapError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 403) return t("site.customDomain.error.entitlement");
      if (err.status === 409) return t("site.customDomain.error.taken");
      if (err.status === 400 || err.status === 422) return t("site.customDomain.error.invalid");
    }
    return t("site.customDomain.error.generic");
  }

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const next = await customDomainApi.connect(venueId, hostname.trim());
      qc.setQueryData(customDomainKeys.domain(venueId), next);
      setHostname("");
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm(t("site.customDomain.disconnectConfirm"))) return;
    setBusy(true);
    setError(null);
    try {
      await customDomainApi.disconnect(venueId);
      qc.setQueryData(customDomainKeys.domain(venueId), null);
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className={section}>
      <legend>{t("site.customDomain.title")}</legend>
      <p className={muted}>{t("site.customDomain.intro")}</p>

      {error && (
        <p role="alert" className={errText}>
          {error}
        </p>
      )}

      {domain ? (
        <ConnectedView domain={domain} busy={busy} onDisconnect={() => void disconnect()} t={t} />
      ) : (
        <div>
          <label className={row} htmlFor="custom-domain-hostname">
            {t("site.customDomain.field.hostname")}
            <input
              id="custom-domain-hostname"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="book.mon-restaurant.fr"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <div className={actions}>
            <Button
              type="button"
              size="sm"
              onClick={() => void connect()}
              disabled={busy || hostname.trim() === ""}
            >
              {t("site.customDomain.connect")}
            </Button>
          </div>
        </div>
      )}
    </fieldset>
  );
}

function ConnectedView({
  domain,
  busy,
  onDisconnect,
  t,
}: {
  domain: CustomDomainResponse;
  busy: boolean;
  onDisconnect: () => void;
  t: (key: string) => string;
}) {
  const pending = PENDING_STATES.has(domain.status);
  return (
    <div>
      <p aria-live="polite">
        <strong>{domain.hostname}</strong> —{" "}
        <span data-testid="custom-domain-status">
          {t(`site.customDomain.status.${domain.status}`)}
        </span>
      </p>

      {pending && (
        <div>
          <p className={muted}>{t("site.customDomain.dns.intro")}</p>
          <dl className={dl}>
            {domain.dcvTxtName && (
              <>
                <dt>{t("site.customDomain.dns.txtRecord")}</dt>
                <dd className={code}>
                  {domain.dcvTxtName} = {domain.dcvTxtValue}
                </dd>
              </>
            )}
            {domain.cnameTarget && (
              <>
                <dt>{t("site.customDomain.dns.cnameRecord")}</dt>
                <dd className={code}>
                  {domain.hostname} → {domain.cnameTarget}
                </dd>
              </>
            )}
          </dl>
          <p className={muted}>{t("site.customDomain.dns.apexHint")}</p>
        </div>
      )}

      {domain.status === "error" && (
        <p role="alert" className={errText}>
          {domain.lastError ?? t("site.customDomain.error.generic")}
        </p>
      )}

      <div className={actions}>
        <Button type="button" size="sm" variant="outline" onClick={onDisconnect} disabled={busy}>
          {t("site.customDomain.disconnect")}
        </Button>
      </div>
    </div>
  );
}
